import { EvaluableCardControlsMenu } from "@client/components/round/allocator/EvaluableCard/EvaluableCardControlsMenu";
import type { FrontendEvaluation } from "@client/components/round/allocator/useEvaluatorEvaluationsCache";
import { Button } from "@client/components/utils/Button";
import { Tooltip } from "@client/components/utils/Tooltip";
import { TriangleDown } from "@client/icons/TriangleDown";
import { TriangleUp } from "@client/icons/TriangleUp";
import { DispatchContext } from "@client/pages/round/Round";
import { formatMoney } from "@client/utils/formatMoney";
import {
  pushRecentProposal,
  RecentIndicator,
} from "@client/utils/recentProposals";
import type { Funder, Recommender } from "@engine/round/aggregate";
import type { MVF } from "@lib/tranche";
import { ldtFromMVF } from "@lib/tranche/trancheUtils";
import type { Evaluator } from "@lib/types";
import { validatePoints } from "@lib/validatePoints";
import { getFullness } from "algorithm/mvfs";
import clsx from "clsx";
import sortBy from "lodash/sortBy";
import React, { useContext, useRef, useState } from "react";
import { toast } from "react-toastify";

type MVFTableSortableFieldName = "name" | "fdv" | "ldt" | "conc";

type MVFTableSort = {
  field: MVFTableSortableFieldName;
  descending: boolean;
};
const defaultSorting: MVFTableSort[] = [
  { field: "name", descending: false },
  { field: "fdv", descending: true },
];

export function MVFTable({
  evaluations,
  evaluator,
}: {
  evaluations: FrontendEvaluation[];
  evaluator: Recommender | Funder;
}) {
  const [sorting, setSorting] = useState<MVFTableSort[]>(defaultSorting);

  const sortedEvaluations = sortEvaluationsBy(evaluations, sorting);

  return (
    <div className="mr-2">
      <h2 className="ml-8">MVF Table</h2>

      <table className="text-xs border-separate border-spacing-0">
        <thead>
          <tr className="sticky top-0 dark:bg-[#181818] z-10">
            <td />
            {["name", "fdv", "ldt", "conc"].map((fieldName) => (
              <SortableFieldHeading
                key={fieldName}
                fieldName={fieldName as MVFTableSortableFieldName}
                defaultDescending={fieldName !== "name"}
                sorting={sorting}
                setSorting={setSorting}
                className="bg-stone-100 dark:bg-[#181818]"
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedEvaluations.map((evaluation) => (
            <MVFTableRow
              key={`${evaluation.evaluableId} + "row"`}
              evaluator={evaluator}
              evaluation={evaluation}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MVFTableRow({
  evaluation,
  evaluator,
}: {
  evaluator: Evaluator;
  evaluation: FrontendEvaluation;
}) {
  const dispatch = useContext(DispatchContext);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const piecewiseTextareaRef = useRef<HTMLTextAreaElement>(null);
  const validationResult = validatePoints(evaluation.mvf, false);

  const validationFailed = validationResult !== null;

  function handlePointsConfirm(evaluation: FrontendEvaluation) {
    const textValue = piecewiseTextareaRef.current?.value;
    try {
      let value = textValue ? JSON.parse(textValue) : [];
      if (Array.isArray(value)) {
        const convertedValue = convertTextToPoints(JSON.stringify(value));
        if (typeof convertedValue === "string") {
          toast.error(`Conversion failed with error: ${convertedValue}`);
          console.error("Conversion failed with error:", convertedValue);
          return;
        }
        value = convertedValue;
      } else {
        toast.error(
          `Expected array for piecewise points, got: ${typeof value}`,
        );
        console.error(
          "Expected array for piecewise points, got:",
          typeof value,
        );
        return;
      }

      handleMVFChange(evaluation, value);
    } catch (e) {
      toast.error(
        `Error parsing MVF: ${e instanceof Error ? e.message : "Unknown error"}`,
      );
      console.error("Error parsing MVF:", e);
    }
  }

  function handleMVFChange(
    evaluation: FrontendEvaluation,
    value: { dollar: number; value: number }[],
  ) {
    void dispatch({
      type: "EVALUATION_MVF_CHANGE",
      evaluatorId: evaluation.evaluatorId,
      evaluableId: evaluation.evaluableId,
      mvf: value.map((pt) => ({
        ...pt,
        placedManually: true,
      })),
    });
    pushRecentProposal(evaluation.evaluableId);

    setIsEditing(false);
  }

  function handleEditClick() {
    setIsEditing(true);
  }

  function onModalOpen() {
    setIsOpen(true);
  }

  function onModalClose() {
    setIsOpen(false);
  }

  return (
    <tr
      className={clsx(
        "relative group",
        validationFailed && "bg-red-400 dark:bg-[#990000]",
      )}
      key={evaluation.evaluableId}
    >
      <TdWithTooltip
        tooltip={validationResult}
        className={clsx(
          "w-5 px-1 cursor-pointer opacity-0 group-hover:opacity-100 fill-white",
          modalIsOpen ? "flex" : "group-hover:flex",
        )}
      >
        <EvaluableCardControlsMenu
          evaluable={evaluation.evaluable}
          evaluator={evaluator}
          onPopperShow={onModalOpen}
          onPopperHide={onModalClose}
          onEditParameters={() => handleEditClick()}
          evaluation={evaluation}
        />
      </TdWithTooltip>
      <TdWithTooltip
        tooltip={validationResult}
        className="border border-gray-500 dark:border-opacity-30 max-w-[180px] text-ellipsis overflow-hidden whitespace-nowrap"
      >
        {evaluation.evaluable.shortname}
        <RecentIndicator proposalId={evaluation.evaluable.id} />
      </TdWithTooltip>
      {isEditing ? (
        <>
          <TdWithTooltip
            tooltip={validationResult}
            colSpan={3}
            className="border border-gray-500 border-opacity-30"
          >
            <textarea
              ref={piecewiseTextareaRef}
              className="w-full dark:bg-[#333] border border-[#444] rounded px-1 py-1 text-xs focus:outline-none focus:border-[#2185d0] focus:shadow-[inset_0_-100px_0_rgba(33,133,208,0.15)]"
              style={{
                height: 10 + evaluation.mvf.length * 16 + "px",
              }}
              defaultValue={convertPointsToText(evaluation.mvf)}
            />
            <div className="flex flex-row">
              <Button onClick={() => handlePointsConfirm(evaluation)}>
                Confirm
              </Button>
              <Button onClick={() => setIsEditing(false)}>Cancel</Button>
            </div>
          </TdWithTooltip>
        </>
      ) : (
        <>
          <TdWithTooltip
            tooltip={validationResult}
            className="border border-gray-500 dark:border-opacity-30 text-right"
          >
            {evaluation.fdv.toFixed(3)}
          </TdWithTooltip>
          <>
            <TdWithTooltip
              tooltip={validationResult}
              className="border border-gray-500 dark:border-opacity-30 text-right"
            >
              {ldtFromMVF(evaluation.mvf) >= 1e12
                ? "Hold"
                : formatMoney(ldtFromMVF(evaluation.mvf))}
            </TdWithTooltip>
            <TdWithTooltip
              tooltip={validationResult}
              className="border border-gray-500 dark:border-opacity-30 text-right"
            >
              {getFullness(evaluation.mvf).toFixed(2)}
            </TdWithTooltip>
          </>
        </>
      )}
    </tr>
  );
}

const TdWithTooltip = ({
  tooltip,
  children,
  ...otherProps
}: {
  tooltip: string | null;
  children: React.ReactNode;
} & React.HTMLProps<HTMLTableCellElement>) => {
  if (tooltip) {
    return (
      <td {...otherProps}>
        <Tooltip title={tooltip}>{children}</Tooltip>
      </td>
    );
  } else {
    return <td {...otherProps}>{children}</td>;
  }
};

function SortableFieldHeading({
  fieldName,
  defaultDescending,
  sorting,
  setSorting,
  className,
}: {
  fieldName: MVFTableSortableFieldName;
  defaultDescending: boolean;
  sorting: MVFTableSort[];
  setSorting: (newSorting: MVFTableSort[]) => void;
  className?: string;
}) {
  function sortByThis() {
    if (sorting?.[0].field === fieldName) {
      setSorting([
        { field: fieldName, descending: !sorting[0].descending },
        ...sorting.slice(1),
      ]);
    } else {
      setSorting([
        { field: fieldName, descending: defaultDescending },
        ...sorting.filter((s) => s.field !== fieldName),
      ]);
    }
  }

  const isSortedFirstByThis = sorting?.[0].field === fieldName;

  const children = {
    name: "Name",
    fdv: (
      <Tooltip title="First Dollar Value" placement="top-start">
        FDV
      </Tooltip>
    ),
    ldt: (
      <Tooltip title="Last Dollar Threshold" placement="top-start">
        LDT
      </Tooltip>
    ),
    conc: (
      <Tooltip title="Concavity" placement="top-start">
        Fullness
      </Tooltip>
    ),
  }[fieldName];

  return (
    <td
      className={clsx(
        "cursor-pointer select-none border-b border-gray-500 dark:border-white border-opacity-50",
        className,
      )}
      onClick={sortByThis}
    >
      {children}

      {isSortedFirstByThis && (
        <>
          {sorting[0].descending ? (
            <TriangleDown className="w-4 h-4 dark:fill-white inline-block ml-1" />
          ) : (
            <TriangleUp className="w-4 h-4 dark:fill-white inline-block ml-1" />
          )}
        </>
      )}
      {!isSortedFirstByThis && <span className="inline-block w-4 ml-1" />}
    </td>
  );
}

function sortEvaluationsBy(
  evaluations: FrontendEvaluation[],
  sorting: MVFTableSort[],
) {
  let sorted = [...evaluations];
  for (let i = sorting.length - 1; i >= 0; i--) {
    sorted = sortBy(sorted, (e) => getFieldForSorting(e, sorting[i]));
    if (sorting[i].descending) {
      sorted.reverse();
    }
  }
  return sorted;
}

function getFieldForSorting(
  evaluation: FrontendEvaluation,
  sorting: MVFTableSort,
) {
  switch (sorting.field) {
    case "name":
      return evaluation.evaluable.shortname;
    case "fdv":
      return evaluation.fdv;
    case "ldt":
      return ldtFromMVF(evaluation.mvf);
    case "conc":
      return getFullness(evaluation.mvf);
  }
}

function convertTextToPoints(
  text: string,
): { value: number; dollar: number }[] | string {
  try {
    let pointsArray: number[][] = JSON.parse(text);

    if (
      pointsArray.length > 0 &&
      pointsArray[pointsArray.length - 1][1] !== 0
    ) {
      const lastX = pointsArray[pointsArray.length - 1][0];
      pointsArray = [...pointsArray, [lastX, 0]];
    }

    const error = validatePoints(pointsArray, false);
    if (error) {
      console.error(error, text);
      return error;
    }

    return pointsArray.map((point) => ({
      value: point[1],
      dollar: point[0],
    }));
  } catch (e) {
    const innerErrorMessage = e instanceof Error ? e.message : "Unknown error";
    const errorMessage = `Error parsing text to points: ${innerErrorMessage}`;
    console.error(errorMessage, text, e);
    return errorMessage;
  }
}

function convertPointsToText(mvf: MVF): string {
  return `[${mvf.map((point) => `[${point.dollar},${point.value}]`).join(",\n")}]`;
}
